import { useOpenReferenceMaterial } from 'core/hooks/useOpenReferenceMaterial';
import { getLessonItems } from 'core/crud/lesson.crud';
import useSystemSettings from 'core/hooks/useSystemSettings';
import { useUser } from 'core/lite/hooks/useUser';
import { getContentLang } from 'core/utils/contentLang';
import { isDocument, isDocumentContent } from 'core/utils/lessonItems';
import { getCurrentReferenceMaterial } from 'core/utils/referenceMaterial';
import { getCttyTypeFromFileName, getValidUrl } from 'core/utils/utils';
import { CONTENT_TYPES } from 'core/variables/constant';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMaterialAccessibleName } from './materialAccessibleName';
import useMaterial from './useMaterial';

export type PreviewCourse = {
  // Explícitas para ser compatible con CourseWithLang (getContentLang);
  // el resto del curso llega sin tipar desde los call-sites legacy.
  program_lang?: string;
  digital_program_langs?: { id?: string }[];
  [key: string]: unknown;
};

type UseDialogMaterialPreviewProps = {
  course: PreviewCourse;
  courseGuid: string;
  lesson: Record<string, unknown>;
  lessonItem: Record<string, unknown>;
  onClickOpenDetailedView: (lessonItem: Record<string, unknown>) => void;
  isStudent: boolean;
  hideReferenceMaterial?: boolean;
};

type DialogSize = 'xxl' | 'l' | 'all-screen';

type UseDialogMaterialPreviewReturn = {
  t: (key: string, options?: Record<string, unknown>) => string;
  accessibleName: string | undefined;
  contentLang: string | undefined;
  material: Record<string, unknown> | null;
  dialogSize: DialogSize;
  isStudentActivityWithAttachments: boolean;
  referenceMaterial: Record<string, unknown> | null;
  attachedFiles: { name: string; size: number; type: string; url: string }[];
  isActiveSeeDetailsButton: boolean;
  isTwoColumn: boolean;
  enableLessonItemComments: boolean;
  handleSuccess: (() => void) | null;
  handleGoToDetailedView: () => void;
  handleShowVisor: () => void;
};

function useDialogMaterialPreview({
  course,
  courseGuid,
  lesson,
  lessonItem,
  onClickOpenDetailedView,
  isStudent,
  hideReferenceMaterial,
}: UseDialogMaterialPreviewProps): UseDialogMaterialPreviewReturn {
  const { t } = useTranslation(['common', 'types-resources', 'course', 'lesson', 'assigments', 'actions']);
  const openReferenceMaterial = useOpenReferenceMaterial(courseGuid);
  const { user, authToken } = useUser();
  const { material } = useMaterial({ course, courseGuid, lesson, lessonItem, onClickOpenDetailedView, user });
  const { enableLessonItemComments } = useSystemSettings();
  const [innerWidth, setInnerWidth] = useState(window.innerWidth);
  const [innerHeight, setInnerHeight] = useState(window.innerHeight);

  const contentLang = getContentLang(course);

  const referenceMaterial = useMemo(() => {
    if (hideReferenceMaterial) return null;
    return getCurrentReferenceMaterial(material);
  }, [material, hideReferenceMaterial]);

  const isStudentActivity =
    isStudent && (material?.content_type_guid === CONTENT_TYPES.TEST || material?.content_type_guid === CONTENT_TYPES.QUESTION);

  const attachedFiles = useMemo(() => {
    if (!material?.attachments?.length) return [];
    return material.attachments.map((attachment: Record<string, unknown>) => {
      const rawUrl = (attachment.url || attachment.encoded_url || null) as string | null;
      return {
        name: attachment.name as string,
        size: attachment.size as number,
        type: getCttyTypeFromFileName(attachment.name as string),
        url: getValidUrl(rawUrl, authToken),
      };
    });
  }, [material?.attachments, authToken]);

  const hasAttachments = !!(referenceMaterial || attachedFiles.length > 0);
  const isStudentActivityWithAttachments = isStudentActivity && hasAttachments;

  const isTwoColumn = (() => {
    // Si es profesor
    if (!isStudent) {
      // Si los comentarios están habilitados, dos columnas para que se vea el panel de mensajes
      // (incluye documentos: PDF, OFFICE, FIXED_LAYOUT)
      if (enableLessonItemComments) return true;

      // Sin comentarios, los documentos ocupan una columna para dar más ancho al visor
      if (isDocument(material?.content_type_guid)) return false;

      return true;
    }

    // Si es estudiante
    if (isStudentActivityWithAttachments) return true;
    if (enableLessonItemComments) return true;
    return !!(material?.content_type_guid && material?.description) || !!referenceMaterial;
  })();

  let dialogSize: DialogSize = 'xxl';
  if (innerWidth < 834 || innerHeight < 660) {
    dialogSize = 'all-screen';
  } else if (!isTwoColumn) {
    dialogSize = 'l';
  }

  const isContentDocument = isDocumentContent(material?.content_type_guid);
  if (isContentDocument && dialogSize !== 'all-screen') {
    dialogSize = 'xxl';
  }

  const isActiveSeeDetailsButton = !!(
    !isStudent && material?.content_type_guid && !isDocument(material?.content_type_guid)
  );

  useEffect(() => {
    if (lessonItem?.lesson_item_guid) {
      getLessonItems(lessonItem.lesson_item_guid as string, course?.guid);
    }
  }, [lessonItem?.lesson_item_guid]);

  useEffect(() => {
    function handleResize() {
      setInnerWidth(window.innerWidth);
      setInnerHeight(window.innerHeight);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  function handleSuccess() {
    onClickOpenDetailedView(lessonItem);
  }

  function handleGoToDetailedView() {
    onClickOpenDetailedView(lessonItem);
  }

  function handleShowVisor() {
    if (referenceMaterial) openReferenceMaterial(referenceMaterial);
  }

  return {
    t,
    accessibleName: getMaterialAccessibleName(material),
    contentLang,
    material,
    dialogSize,
    isStudentActivityWithAttachments,
    referenceMaterial,
    attachedFiles,
    isActiveSeeDetailsButton,
    isTwoColumn,
    enableLessonItemComments,
    handleSuccess: isActiveSeeDetailsButton ? handleSuccess : null,
    handleGoToDetailedView,
    handleShowVisor,
  };
}

export default useDialogMaterialPreview;
